// Firebase app + auth singleton.
//
// Firebase owns identity only. Whether an authenticated person may actually see
// anything is decided by the gait API against its `users` table — a valid token
// with no row there is rejected with 403. So none of the config below is a
// secret; it identifies the project, it does not authorise access to it.
import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, type Auth } from 'firebase/auth';

const config: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/** True when the build actually carried a Firebase config through. */
export const isFirebaseConfigured = Boolean(config.apiKey && config.projectId);

// A build with the env vars missing must not take the whole page down.
//
// The first version of this logged a warning and then called getAuth() anyway,
// which throws auth/invalid-api-key during module evaluation — before React
// mounts. The result was a blank screen with the explanation only in the
// console, which is the least useful failure possible. Observed for real on a
// Cloudflare preview deployment, where the variables had been set for the
// Production environment but not Preview.
//
// So: never construct the SDK with a config we know is invalid, and let the UI
// say what is wrong.
export const auth: Auth | null = (() => {
  if (!isFirebaseConfigured) {
    console.error(
      'Firebase is not configured — VITE_FIREBASE_API_KEY / VITE_FIREBASE_PROJECT_ID ' +
        'were empty at build time. Set them on the Pages project (for BOTH the ' +
        'Production and Preview environments) and redeploy.'
    );
    return null;
  }
  try {
    const instance = getAuth(initializeApp(config));
    // Survive a page reload; the ID token is short-lived and refreshed by the
    // SDK. Explicit rather than relying on the default, which has changed across
    // major versions — a silent switch to session persistence would log everyone
    // out on every reload.
    void setPersistence(instance, browserLocalPersistence);
    return instance;
  } catch (err) {
    // A malformed-but-present key lands here rather than in the branch above.
    console.error('Firebase failed to initialise:', err);
    return null;
  }
})();
